import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("請輸入信箱與密碼");
      return;
    }

    setIsLoading(true);

    // Safety: auto-release loading after 15 seconds no matter what
    const safetyTimer = setTimeout(() => {
      setIsLoading(false);
      setError("登入逾時，請檢查網路後重新嘗試");
    }, 15000);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      clearTimeout(safetyTimer);

      if (authError) {
        setIsLoading(false);
        setError(
          authError.message === "Invalid login credentials"
            ? "信箱或密碼錯誤，請重新輸入"
            : authError.message,
        );
        return;
      }

      setIsLoading(false);
      navigate("/", { replace: true });
    } catch (err: any) {
      clearTimeout(safetyTimer);
      setIsLoading(false);
      setError(err?.message || "登入失敗，請稍後再試");
    }
  };

  // ===== LOGIN VIEW =====
  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/login-bg.png)",
        }}
      />
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/40" />

      <div className="w-full max-w-md relative z-10">
        <div className="bg-background-50/95 backdrop-blur-sm border border-background-200/50 rounded-2xl p-6 md:p-8 shadow-xl">
          <h2 className="text-lg font-semibold text-foreground-900 mb-6">
            登入系統
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                電子信箱
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-foreground-400 text-sm" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="請輸入信箱"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                密碼
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-foreground-400 text-sm" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="請輸入密碼"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-accent-600 bg-accent-50 px-3 py-2 rounded-lg">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  登入中...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  登入
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-background-200">
            <p className="text-xs text-foreground-500 text-center">
              如需開通帳號，請聯繫總管理員
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}